import { Drawer } from 'antd';
import { Button } from 'components/Button';
import ChoiceChips from 'components/Form/choiceChips';
import DateRangeFields from 'components/Form/dateRangeFields';
import FilterSection, { FilterField } from 'components/Form/filterSection';
import FilterSelect from "components/Form/filterSelect";
import { useCallback, useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useMediaQuery } from 'react-responsive';
import { activeStatusOptions, eosOptions, genderOptions, maritalStatusOptions } from 'utils/constants';

const FilterDrawer = ({
   
    searchPlaceholder,
    handleFilterDrawer,
    showFilterDrawer,

    handleFilterChange,
    filters,
    handleInputChange,
    handleApplyFilter,
    filterLoading,
    handleDateChange,
    handleClearAll,
    isFilterDirty

}) => {
  const [placement] = useState('right');  
  const isMobile = useMediaQuery({ query: "(max-width: 768px)" });
   const {
    department, departmentLoading,
    reportTo, reportToLoading,
    nationality, nationalityLoading,
    position, positionLoading,
    division, divisionLoading,
    costCenter, costCenterLoading
} = useSelector((state) => state.common);

const [reportOption, setReportOption] = useState([]);
const [departmentOption, setDepartmentOption] = useState([]);
const [nationalityOption, setNationalityOption] = useState([]);
const [positionOption, setPositionOption] = useState([]);
const [divisionOption, setDivisionOption] = useState([]);

const [costCenterOption, setCostCenterOption] = useState([]);


useEffect(()=>{
    let _reports = reportTo.map(item=> {
        let name = `${item.first_name} ${item.last_name || ""}`.trim()
        return{
            label: `${item.emp_no ? item.emp_no + " - " : ""}${name}${item.is_active === false ? " (Inactive)" : ""}`,
            value: item.user_id,
            // Extra fields make the option searchable by emp_no/email
            // via FilterSelect's filterOption.
            emp_no: item.emp_no,
            email: item.email
        }
    })
    let _departments = department.map(item=> {
        return{
            label: item.name,
            value: item.id
        }
    })
    let _nationality = nationality.map(item=> {
        return{
            label: item.name,
            value: item.id
        }
    })
    let _position = position.map(item=> {
        return{
            label: item.name,
            value: item.id
        }
    })
    let _division = division.map(item=> {
        return{
            label: item.name,
            value: item.id
        }
    })

    let cost_center = costCenter.map(item=> {
      return{
          label: item.name || item.code,
          value: item.id,
          // Not shown, but FilterSelect's filterOption matches it, so
          // typing a store code still finds the option.
          code: item.code
      }
  })
  
    setReportOption(_reports)
    setDepartmentOption(_departments)
    setNationalityOption(_nationality)
    setPositionOption(_position)
    setDivisionOption(_division)
    setCostCenterOption(cost_center)
},[department, reportTo, nationality, position, division, costCenter])





   const MULTI_OPTIONS = {
    nationality: () => nationalityOption,
    cost_center: () => costCenterOption,
    department: () => departmentOption,
    position: () => positionOption,
    report_to: () => reportOption,
   }

   const setFilterValue = useCallback((key) => {
    if(filters.length){
        let selectedFilter = [...filters].find(item=> item.key === key)
        if(selectedFilter?.value){
          if(MULTI_OPTIONS[key]){
            // Multi-select: the value may be an array of option objects (from
            // the control), an array of ids, or a comma-separated id string
            // (from the URL). Resolve all of them to option objects.
            const optionsFor = MULTI_OPTIONS[key]()
            const raw = selectedFilter.value
            const ids = Array.isArray(raw)
              ? raw.map(val => (typeof val === 'object' && val !== null ? val.value : val))
              : String(raw).split(',').map(id => id.trim())
            return ids
              .filter(id => id !== '' && id !== null && id !== undefined)
              .map(id => optionsFor.find(opt => String(opt.value) === String(id)) || { value: id, label: String(id) })
          }
          
          // For single select, find the matching option object from the options array
          let filterValue = selectedFilter.value
          
          // Convert to string for comparison if needed
          if(typeof filterValue === 'number'){
            filterValue = String(filterValue)
          }
          
          switch(key){
            case "gender":
              return genderOptions.find(opt => String(opt.value) === String(filterValue)) || null
            case "report_to":
              return reportOption.find(opt => String(opt.value) === String(filterValue)) || null
            case "department":
              return departmentOption.find(opt => String(opt.value) === String(filterValue)) || null
            case "position":
              return positionOption.find(opt => String(opt.value) === String(filterValue)) || null
            case "marital_status":
              return maritalStatusOptions.find(opt => String(opt.value) === String(filterValue)) || null
            case "cost_center":
              return costCenterOption.find(opt => String(opt.value) === String(filterValue)) || null
            case "status":
              return [{label: "All", value: "all"},...activeStatusOptions].find(opt => String(opt.value) === String(filterValue)) || null
            case "eos_approved":
              return eosOptions.find(opt => String(opt.value) === String(filterValue)) || null
            default:
              return null
          }
        }
    }

    return null

  }, [filters, genderOptions, reportOption, departmentOption, positionOption, divisionOption, maritalStatusOptions, costCenterOption, activeStatusOptions, eosOptions, nationalityOption]);

  // Raw filter values for the chip groups and the per-section counts.
  const valueOf = (key) => filters.find((f) => f.key === key)?.value ?? "";
  const hasValue = (key) => {
    const v = valueOf(key);
    return Array.isArray(v) ? v.length > 0 : v !== "" && v !== null && v !== undefined;
  };
  const SECTION_KEYS = {
    employment: ["status", "employment_date_after", "employment_date_before", "resigned_date_after", "resigned_date_before", "eos_approved"],
    organisation: ["cost_center", "department", "position", "report_to"],
    personal: ["gender", "marital_status", "nationality"],
  };
  const countOf = (keys) => keys.filter(hasValue).length;
  const activeCount = Object.values(SECTION_KEYS).flat().filter(hasValue).length;
  const statusOptions = [{ label: "All", value: "all" }, ...activeStatusOptions];

  return (
    <>
      <Drawer
        className="filter-drawer"
        title={
          <span className="filter-drawer__title">
            Filters
            {activeCount > 0 && <span className="filter-section__count" aria-label={`${activeCount} active`}>{activeCount}</span>}
          </span>
        }
        placement={placement}
        width={isMobile ? "100%" : 440}
        closable={true}
        onClose={handleFilterDrawer}
        open={showFilterDrawer}
        key={placement}
        footer={
          <div className="filter-drawer__footer">
            <Button
              label="Clear all"
              onClick={handleClearAll}
              disabled={activeCount === 0}
              className="btn btn-link p-0"
              style={{ color: activeCount ? "var(--brand-text-muted)" : "#d1d5db", fontWeight: 600 }}
            />
            <Button
              label="Apply Filter"
              loading={filterLoading}
              disabled={filterLoading || !isFilterDirty}
              onClick={handleApplyFilter}
              className="btn btn-primary"
              style={{ borderRadius: "8px" }}
            />
          </div>
        }
      >
        <FilterSection title="Employment" count={countOf(SECTION_KEYS.employment)}>
          <FilterField label="Status">
            <ChoiceChips
              ariaLabel="Employee status"
              options={statusOptions}
              value={valueOf("status")}
              onChange={(option) => handleFilterChange(option, "status")}
            />
          </FilterField>
          <DateRangeFields
            label="Joined"
            fromId="employment_date_after"
            toId="employment_date_before"
            fromValue={valueOf("employment_date_after")}
            toValue={valueOf("employment_date_before")}
            onFromChange={(v) => handleDateChange(v, "employment_date_after")}
            onToChange={(v) => handleDateChange(v, "employment_date_before")}
          />
          <DateRangeFields
            label="Resigned"
            fromId="resigned_date_after"
            toId="resigned_date_before"
            fromValue={valueOf("resigned_date_after")}
            toValue={valueOf("resigned_date_before")}
            onFromChange={(v) => handleDateChange(v, "resigned_date_after")}
            onToChange={(v) => handleDateChange(v, "resigned_date_before")}
          />
          <FilterField label="End of service approved">
            <ChoiceChips
              ariaLabel="End of service approved"
              options={eosOptions}
              value={valueOf("eos_approved")}
              onChange={(option) => handleFilterChange(option, "eos_approved")}
            />
          </FilterField>
        </FilterSection>

        <FilterSection title="Organisation" count={countOf(SECTION_KEYS.organisation)}>
          <FilterField label="Cost center" hint="one or more" htmlFor="filter-cost-center">
            <FilterSelect inputId="filter-cost-center" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "cost_center")}} optionLabel={"label"} optionValue={'value'} options={costCenterOption} value={setFilterValue("cost_center")} placeholder={"Any"} isLoading={costCenterLoading} noOptionsLabel={"cost centers"}/>
          </FilterField>
          <FilterField label="Department" hint="one or more" htmlFor="filter-department">
            <FilterSelect inputId="filter-department" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "department")}} optionLabel={"label"} optionValue={'value'} options={departmentOption} isLoading={departmentLoading} value={setFilterValue("department")} placeholder={"Any"} noOptionsLabel={"departments"}/>
          </FilterField>
          <FilterField label="Designation" hint="one or more" htmlFor="filter-position">
            <FilterSelect inputId="filter-position" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "position")}} optionLabel={"label"} optionValue={'value'} options={positionOption} isLoading={positionLoading} value={setFilterValue("position")} placeholder={"Any"} noOptionsLabel={"designations"}/>
          </FilterField>
          <FilterField label="Reports to" hint="one or more" htmlFor="filter-report-to">
            <FilterSelect inputId="filter-report-to" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "report_to")}} optionLabel={"label"} optionValue={'value'} isLoading={reportToLoading} value={setFilterValue("report_to")} options={reportOption} placeholder={"Any manager"} noOptionsLabel={"managers"}/>
          </FilterField>
        </FilterSection>

        <FilterSection title="Personal" count={countOf(SECTION_KEYS.personal)}>
          <FilterField label="Nationality" hint="one or more" htmlFor="filter-nationality">
            <FilterSelect inputId="filter-nationality" isMulti={true} checkboxMenu={true} maxVisibleValues={2} menuPosition="fixed" isClearable={true} onChange={e=>{handleFilterChange(e, "nationality")}} optionLabel={"label"} optionValue={'value'} options={nationalityOption} isLoading={nationalityLoading} value={setFilterValue("nationality")} placeholder={"Any"} noOptionsLabel={"nationalities"}/>
          </FilterField>
          <FilterField label="Gender">
            <ChoiceChips
              ariaLabel="Gender"
              options={genderOptions}
              value={valueOf("gender")}
              onChange={(option) => handleFilterChange(option, "gender")}
            />
          </FilterField>
          <FilterField label="Marital status">
            <ChoiceChips
              ariaLabel="Marital status"
              options={maritalStatusOptions}
              value={valueOf("marital_status")}
              onChange={(option) => handleFilterChange(option, "marital_status")}
            />
          </FilterField>
        </FilterSection>
      </Drawer>
    </>
  );
};
export default FilterDrawer;
